import { useEffect, useState } from "react";
import type { PublicEvent } from "@vzlive/shared";
import { t } from "@vzlive/shared/i18n";
import { apiFetch } from "../lib/api";
import { tenantTheme } from "../lib/branding";

interface TenantEventsResponse {
  tenant: {
    name: string;
    logoUrl: string | null;
    primaryColor: string;
    secondaryColor: string;
    locale: "de" | "en";
    legal: { imprintUrl?: string; privacyUrl?: string };
  };
  events: PublicEvent[];
  productHost: string;
}

/**
 * `/embed/archive` — lists finished events of this tenant as native cards,
 * linking to the public reader. Lives inside an auto-height iframe, so the
 * host page simply grows; includes the required standalone branding/legal.
 */
export function EmbedArchivePage() {
  const [tenant, setTenant] = useState<TenantEventsResponse["tenant"] | null>(
    null,
  );
  const [archive, setArchive] = useState<PublicEvent[]>([]);
  const [productHost, setProductHost] = useState("");
  const [state, setState] = useState<"loading" | "ready" | "error">("loading");

  useEffect(() => {
    let cancelled = false;
    apiFetch<TenantEventsResponse>("/api/public/tenant")
      .then((payload) => {
        if (cancelled) return;
        const sorted = [...payload.events]
          .filter((event) => event.status === "archived")
          .sort(
            (first, second) =>
              Date.parse(second.startsAt) - Date.parse(first.startsAt),
          );
        setTenant(payload.tenant);
        setArchive(sorted);
        setProductHost(payload.productHost);
        setState("ready");
        document.documentElement.lang = payload.tenant.locale;
        document.title = `Archiv · ${payload.tenant.name}`;
      })
      .catch(() => {
        if (!cancelled) setState("error");
      });
    const postHeight =
      window.parent === window
        ? undefined
        : () => {
            window.parent.postMessage(
              {
                type: "vzlive:height",
                height: document.documentElement.scrollHeight,
              },
              "*",
            );
          };
    if (postHeight) {
      const observer = new ResizeObserver(postHeight);
      observer.observe(document.body);
      postHeight();
      return () => {
        cancelled = true;
        observer.disconnect();
      };
    }
    return () => {
      cancelled = true;
    };
  }, []);

  if (state === "loading") return null;
  if (state === "error" || !tenant)
    return (
      <main className="reader-error" id="main">
        <h1>Archiv nicht verfügbar.</h1>
      </main>
    );

  const locale = tenant.locale;
  return (
    <div
      className="reader-page reader-page--embed archive-page"
      style={tenantTheme(tenant.primaryColor, tenant.secondaryColor)}
      lang={locale}
    >
      <header className="archive-page__header">
        {tenant.logoUrl ? (
          <img src={tenant.logoUrl} alt={tenant.name} />
        ) : (
          <strong>{tenant.name}</strong>
        )}
        <h1>Archiv</h1>
      </header>
      <main className="archive-page__main" id="main">
        {archive.length === 0 ? (
          <p role="status">
            {locale === "en"
              ? "No archived events yet."
              : "Noch keine archivierten Events."}
          </p>
        ) : (
          <ul className="archive-page__list" aria-label="Archivierte Events">
            {archive.map((event) => (
              <li key={event.id}>
                <a
                  className="archive-page__card"
                  href={`/events/${event.slug}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  <span className="archive-page__state">
                    <i aria-hidden="true" /> {t(locale, "archived")}
                  </span>
                  <strong>{event.title}</strong>
                  <small>
                    <time dateTime={event.startsAt}>
                      {new Intl.DateTimeFormat(locale, {
                        dateStyle: "long",
                        hour: "2-digit",
                        minute: "2-digit",
                        timeZone: event.timezone,
                      }).format(new Date(event.startsAt))}
                    </time>
                    {event.venue && <span> · {event.venue}</span>}
                  </small>
                </a>
              </li>
            ))}
          </ul>
        )}
      </main>
      <footer className="embed-widget__footer">
        <strong>{tenant.name}</strong>
        <a
          className="embed-widget__product"
          href={`https://${productHost}/`}
          target="_blank"
          rel="noreferrer"
        >
          VZ.live
        </a>
      </footer>
    </div>
  );
}
